import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, TrendingDown, Package, DollarSign, AlertTriangle, BarChart3, Users, Clock, Download, FileText, Activity, Mail } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function Reports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profit-loss');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [days, setDays] = useState(30);
  const [profitLoss, setProfitLoss] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [lowStock, setLowStock] = useState(null);
  const [customerSales, setCustomerSales] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [stockMovements, setStockMovements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmailReport, setShowEmailReport] = useState(false);
  const [reportEmail, setReportEmail] = useState('');

  const handleEmailReport = async () => {
    if (!reportEmail.trim()) { alert('Enter an email address'); return; }
    setExporting(true);
    try {
      const exportMap = {
        'profit-loss': '/api/reports/profit-loss/export',
        'valuation': '/api/reports/inventory-valuation/export',
        'top-products': '/api/reports/top-products/export',
        'low-stock': '/api/reports/low-stock/export',
        'customer-sales': '/api/reports/customer-sales/export',
        'receivables': '/api/reports/receivables-aging/export',
        'stock-movements': '/api/reports/stock-movements/export',
      };
      const tabNames = {
        'profit-loss': 'Profit & Loss', 'valuation': 'Inventory Valuation', 'top-products': 'Top Products',
        'low-stock': 'Low Stock', 'customer-sales': 'Customer Sales', 'receivables': 'Receivables Aging',
        'stock-movements': 'Stock Movements',
      };
      let url = exportMap[activeTab] + '?format=pdf';
      if (['profit-loss', 'top-products', 'customer-sales', 'stock-movements'].includes(activeTab)) {
        if (fromDate) url += `&from_date=${fromDate}`;
        if (toDate) url += `&to_date=${toDate}`;
        if (!fromDate && !toDate) url += `&days=${days}`;
      }
      const token = localStorage.getItem('access_token');
      await api.request('/api/reports/email', {
        method: 'POST',
        body: JSON.stringify({ email: reportEmail, report_url: url, report_name: tabNames[activeTab], token }),
      });
      alert(`Report emailed to ${reportEmail}`);
      setShowEmailReport(false);
      setReportEmail('');
    } catch (err) { alert('Email failed: ' + err.message); }
    finally { setExporting(false); }
  };

  const loadReport = useCallback(async (tab) => {
    setLoading(true);
    try {
      if (tab === 'profit-loss') {
        const data = await api.getProfitLossReport(fromDate || null, toDate || null, days);
        setProfitLoss(data);
      } else if (tab === 'valuation') {
        const data = await api.getInventoryValuation();
        setValuation(data);
      } else if (tab === 'top-products') {
        const data = await api.getTopProductsReport(fromDate || null, toDate || null, days);
        setTopProducts(data);
      } else if (tab === 'low-stock') {
        const data = await api.getLowStockReport();
        setLowStock(data);
      } else if (tab === 'customer-sales') {
        const data = await api.getCustomerSalesReport(fromDate || null, toDate || null, days);
        setCustomerSales(data);
      } else if (tab === 'receivables') {
        const data = await api.getReceivablesAgingReport();
        setReceivables(data);
      } else if (tab === 'stock-movements') {
        const data = await api.request(`/api/reports/stock-movements?${fromDate ? `from_date=${fromDate}` : `days=${days}`}${toDate ? `&to_date=${toDate}` : ''}`);
        setStockMovements(data);
      }
    } catch (err) {
      console.error('Report error:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, days]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const exportMap = {
        'profit-loss': '/api/reports/profit-loss/export',
        'valuation': '/api/reports/inventory-valuation/export',
        'top-products': '/api/reports/top-products/export',
        'low-stock': '/api/reports/low-stock/export',
        'customer-sales': '/api/reports/customer-sales/export',
        'receivables': '/api/reports/receivables-aging/export',
        'stock-movements': '/api/reports/stock-movements/export',
      };
      const endpoint = exportMap[activeTab];
      const params = { format };
      if (['profit-loss', 'top-products', 'customer-sales', 'stock-movements'].includes(activeTab)) {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        if (!fromDate && !toDate) params.days = days;
      }
      await api.downloadReportExport(endpoint, params);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadReport(activeTab);
  }, [activeTab, loadReport]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  const tabs = [
    { id: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp },
    { id: 'valuation', label: 'Inventory Valuation', icon: Package },
    { id: 'top-products', label: 'Top Products', icon: BarChart3 },
    { id: 'low-stock', label: 'Low Stock Alert', icon: AlertTriangle },
    { id: 'customer-sales', label: 'Customer Sales', icon: Users },
    { id: 'receivables', label: 'Receivables Aging', icon: Clock },
    { id: 'stock-movements', label: 'Stock Movements', icon: Activity },
    { id: 'inventory-counts', label: 'Inventory Counts', icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
        <div className="flex gap-2">
          <button
            data-testid="export-csv-btn"
            onClick={() => handleExport('csv')}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
          <button
            data-testid="export-pdf-btn"
            onClick={() => handleExport('pdf')}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <FileText className="h-3.5 w-3.5" />
            PDF
          </button>
          <button
            data-testid="email-report-btn"
            onClick={() => setShowEmailReport(!showEmailReport)}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Mail className="h-3.5 w-3.5" />
            Email
          </button>
        </div>
      </div>

      {/* Email Report Bar */}
      {showEmailReport && (
        <div className="bg-card border border-border rounded-lg px-4 py-3 flex gap-3 items-end">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Email report to:</label>
            <input type="email" value={reportEmail} onChange={e => setReportEmail(e.target.value)}
              placeholder="email@example.com" className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="report-email-input" />
          </div>
          <button onClick={handleEmailReport} disabled={exporting}
            className="px-4 py-2 bg-primary text-white rounded-md text-sm disabled:opacity-50" data-testid="send-report-email-btn">
            {exporting ? 'Sending...' : 'Send'}
          </button>
          <button onClick={() => setShowEmailReport(false)} className="px-3 py-2 border rounded-md text-sm hover:bg-muted">Cancel</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit" data-testid="report-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => {
                if (tab.id === 'inventory-counts') {
                  navigate('/inventory-reports');
                  return;
                }
                setActiveTab(tab.id);
              }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Date filter for applicable reports */}
      {(activeTab === 'profit-loss' || activeTab === 'top-products' || activeTab === 'customer-sales' || activeTab === 'stock-movements') && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">From Date</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="block mt-1 px-3 py-1.5 text-sm border rounded-md bg-background" data-testid="from-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">To Date</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="block mt-1 px-3 py-1.5 text-sm border rounded-md bg-background" data-testid="to-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Quick Range</label>
                <select value={days} onChange={(e) => { setDays(parseInt(e.target.value)); setFromDate(''); setToDate(''); }}
                  className="block mt-1 px-3 py-1.5 text-sm border rounded-md bg-background" data-testid="quick-range">
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last year</option>
                </select>
              </div>
              <button onClick={() => loadReport(activeTab)}
                className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90" data-testid="apply-filter-btn">
                Apply
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <div className="text-center py-12 text-muted-foreground">Loading report...</div>}

      {/* Profit & Loss */}
      {activeTab === 'profit-loss' && profitLoss && !loading && (
        <div className="space-y-6" data-testid="profit-loss-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <DollarSign className="h-3.5 w-3.5" /> Revenue
                </div>
                <p className="text-xl font-bold" data-testid="total-revenue">{fmt(profitLoss.revenue.total)}</p>
                <p className="text-xs text-muted-foreground">{profitLoss.revenue.order_count} orders</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Package className="h-3.5 w-3.5" /> COGS
                </div>
                <p className="text-xl font-bold">{fmt(profitLoss.cost_of_goods_sold)}</p>
                <p className="text-xs text-muted-foreground">{profitLoss.items_sold} items sold</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  {profitLoss.gross_profit >= 0 ? <TrendingUp className="h-3.5 w-3.5 text-green-500" /> : <TrendingDown className="h-3.5 w-3.5 text-red-500" />}
                  Gross Profit
                </div>
                <p className={`text-xl font-bold ${profitLoss.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="gross-profit">
                  {fmt(profitLoss.gross_profit)}
                </p>
                <p className="text-xs text-muted-foreground">{profitLoss.gross_margin_percent}% margin</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <DollarSign className="h-3.5 w-3.5" /> Net Profit
                </div>
                <p className={`text-xl font-bold ${profitLoss.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {fmt(profitLoss.net_profit)}
                </p>
                <p className="text-xs text-muted-foreground">After fees: {fmt(profitLoss.revenue.square_fees)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue breakdown */}
          <Card>
            <CardHeader><CardTitle className="text-base">Revenue Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal (Product Sales)</span><span className="font-medium">{fmt(profitLoss.revenue.subtotal)}</span></div>
                <div className="flex justify-between"><span>Tax Collected</span><span className="font-medium">{fmt(profitLoss.revenue.tax_collected)}</span></div>
                <div className="flex justify-between"><span>Freight/Shipping</span><span className="font-medium">{fmt(profitLoss.revenue.freight)}</span></div>
                <div className="flex justify-between border-t pt-2 font-semibold"><span>Total Revenue</span><span>{fmt(profitLoss.revenue.total)}</span></div>
                <div className="flex justify-between pt-4"><span>Cost of Goods Sold</span><span className="font-medium text-red-600">-{fmt(profitLoss.cost_of_goods_sold)}</span></div>
                <div className="flex justify-between"><span>Square Processing Fees</span><span className="font-medium text-red-600">-{fmt(profitLoss.revenue.square_fees)}</span></div>
                <div className="flex justify-between border-t pt-2 font-bold text-base">
                  <span>Net Profit</span>
                  <span className={profitLoss.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}>{fmt(profitLoss.net_profit)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Purchases */}
          <Card>
            <CardHeader><CardTitle className="text-base">Purchase Orders</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>Total Purchases</span><span className="font-medium">{fmt(profitLoss.purchases.total)}</span></div>
                <div className="flex justify-between"><span>PO Count</span><span className="font-medium">{profitLoss.purchases.po_count}</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Daily revenue chart */}
          {profitLoss.daily_revenue && profitLoss.daily_revenue.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Daily Revenue Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40" data-testid="daily-chart">
                  {profitLoss.daily_revenue.map((d, i) => {
                    const maxRev = Math.max(...profitLoss.daily_revenue.map(x => x.total));
                    const height = maxRev > 0 ? (d.total / maxRev) * 100 : 0;
                    return (
                      <div key={d._id || i} className="flex-1 flex flex-col items-center gap-1" title={`${d._id}: ${fmt(d.total)} (${d.count} orders)`}>
                        <div className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary" style={{ height: `${Math.max(height, 2)}%` }} />
                        {profitLoss.daily_revenue.length <= 14 && (
                          <span className="text-[9px] text-muted-foreground rotate-[-45deg] w-8 text-center">{d._id.slice(5)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Inventory Valuation */}
      {activeTab === 'valuation' && valuation && !loading && (
        <div className="space-y-6" data-testid="valuation-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Cost Value (PWO Cost)</p>
              <p className="text-xl font-bold" data-testid="cost-value">{fmt(valuation.summary.total_cost_value)}</p>
              <p className="text-[10px] text-muted-foreground">Cost before markup</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Retail Value (Non-PWO)</p>
              <p className="text-xl font-bold">{fmt(valuation.summary.total_retail_value)}</p>
              <p className="text-[10px] text-muted-foreground">With markup applied</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Potential Profit</p>
              <p className="text-xl font-bold text-green-600">{fmt(valuation.summary.potential_profit)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total SKUs / Items</p>
              <p className="text-xl font-bold">{valuation.summary.total_skus.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{valuation.summary.total_items.toLocaleString()} units in stock</p>
            </CardContent></Card>
          </div>

          {/* By category */}
          <Card>
            <CardHeader><CardTitle className="text-base">Value by Category</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">Category</th>
                    <th className="pb-2 font-medium text-right">SKUs</th>
                    <th className="pb-2 font-medium text-right">Items</th>
                    <th className="pb-2 font-medium text-right">Cost Value</th>
                    <th className="pb-2 font-medium text-right">Retail Value</th>
                  </tr></thead>
                  <tbody>
                    {valuation.by_category.map((cat, i) => (
                      <tr key={cat.name || `cat-${i}`} className="border-b border-border/50">
                        <td className="py-2 font-medium">{cat.name || 'Uncategorized'}</td>
                        <td className="py-2 text-right">{cat.skus}</td>
                        <td className="py-2 text-right">{cat.items}</td>
                        <td className="py-2 text-right">{fmt(cat.cost_value)}</td>
                        <td className="py-2 text-right">{fmt(cat.retail_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Top items by value */}
          <Card>
            <CardHeader><CardTitle className="text-base">Top Items by Value (Cost)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">Product</th>
                    <th className="pb-2 font-medium">SKU</th>
                    <th className="pb-2 font-medium text-right">Qty</th>
                    <th className="pb-2 font-medium text-right">Cost</th>
                    <th className="pb-2 font-medium text-right">Total Value</th>
                  </tr></thead>
                  <tbody>
                    {valuation.items.slice(0, 20).map((item, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1.5 max-w-[200px] truncate">{item.name}</td>
                        <td className="py-1.5 text-muted-foreground">{item.sku}</td>
                        <td className="py-1.5 text-right">{item.quantity}</td>
                        <td className="py-1.5 text-right">{fmt(item.cost_price)}</td>
                        <td className="py-1.5 text-right font-medium">{fmt(item.cost_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Products */}
      {activeTab === 'top-products' && topProducts && !loading && (
        <div className="space-y-6" data-testid="top-products-report">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-500" /> Top Products by Quantity Sold
            </CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">#</th>
                    <th className="pb-2 font-medium">Product</th>
                    <th className="pb-2 font-medium">SKU</th>
                    <th className="pb-2 font-medium text-right">Qty Sold</th>
                    <th className="pb-2 font-medium text-right">Orders</th>
                    <th className="pb-2 font-medium text-right">Revenue</th>
                  </tr></thead>
                  <tbody>
                    {topProducts.by_quantity.map((p, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 font-medium max-w-[200px] truncate">{p.name}</td>
                        <td className="py-1.5 text-muted-foreground">{p.sku}</td>
                        <td className="py-1.5 text-right font-bold text-blue-600">{p.total_quantity}</td>
                        <td className="py-1.5 text-right">{p.order_count}</td>
                        <td className="py-1.5 text-right text-muted-foreground">{fmt(p.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Low Stock */}
      {activeTab === 'low-stock' && lowStock && !loading && (
        <div className="space-y-6" data-testid="low-stock-report">
          <div className="grid grid-cols-3 gap-4">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Low Stock</p>
              <p className="text-2xl font-bold text-amber-600" data-testid="total-low-stock">{lowStock.summary.total_low_stock}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{lowStock.summary.out_of_stock}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Below Reorder Point</p>
              <p className="text-2xl font-bold text-amber-500">{lowStock.summary.below_reorder_point}</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Low Stock Items</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card"><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">Product</th>
                    <th className="pb-2 font-medium">SKU</th>
                    <th className="pb-2 font-medium text-right">On Hand</th>
                    <th className="pb-2 font-medium text-right">Reorder Pt</th>
                    <th className="pb-2 font-medium text-right">Shortage</th>
                    <th className="pb-2 font-medium">Vendor</th>
                  </tr></thead>
                  <tbody>
                    {lowStock.items.slice(0, 100).map((item, i) => (
                      <tr key={item.id || item.sku || i} className="border-b border-border/50">
                        <td className="py-1.5 max-w-[200px] truncate font-medium">{item.name}</td>
                        <td className="py-1.5 text-muted-foreground">{item.sku}</td>
                        <td className={`py-1.5 text-right font-medium ${item.quantity_on_hand === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {item.quantity_on_hand}
                        </td>
                        <td className="py-1.5 text-right">{item.reorder_point}</td>
                        <td className="py-1.5 text-right text-red-600 font-medium">{item.shortage}</td>
                        <td className="py-1.5 text-muted-foreground max-w-[150px] truncate">{item.last_vendor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Customer Sales */}
      {activeTab === 'customer-sales' && customerSales && !loading && (
        <div className="space-y-6" data-testid="customer-sales-report">
          <div className="grid grid-cols-3 gap-4">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Customers</p>
              <p className="text-2xl font-bold" data-testid="total-customers">{customerSales.summary.total_customers}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{fmt(customerSales.summary.total_revenue)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Orders</p>
              <p className="text-2xl font-bold">{customerSales.summary.total_orders}</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" /> Top Customers by Revenue
            </CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="customer-sales-table">
                  <thead className="sticky top-0 bg-card"><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">#</th>
                    <th className="pb-2 font-medium">Customer</th>
                    <th className="pb-2 font-medium text-right">Revenue</th>
                    <th className="pb-2 font-medium text-right">Orders</th>
                    <th className="pb-2 font-medium text-right">Avg Order</th>
                    <th className="pb-2 font-medium text-right">Tax</th>
                    <th className="pb-2 font-medium">Last Order</th>
                  </tr></thead>
                  <tbody>
                    {customerSales.customers.map((c, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1.5 text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 font-medium max-w-[200px] truncate">{c.customer_name}</td>
                        <td className="py-1.5 text-right font-medium text-green-600">{fmt(c.total_revenue)}</td>
                        <td className="py-1.5 text-right">{c.order_count}</td>
                        <td className="py-1.5 text-right">{fmt(c.avg_order)}</td>
                        <td className="py-1.5 text-right text-muted-foreground">{fmt(c.total_tax)}</td>
                        <td className="py-1.5 text-muted-foreground text-sm">{c.last_order_date ? new Date(c.last_order_date).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Receivables Aging */}
      {activeTab === 'receivables' && receivables && !loading && (
        <div className="space-y-6" data-testid="receivables-report">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Current (0-30d)</p>
              <p className="text-lg font-bold tabular-nums">{fmt(receivables.summary.current)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">31-60 Days</p>
              <p className="text-lg font-bold tabular-nums text-amber-600">{fmt(receivables.summary['30_days'])}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">61-90 Days</p>
              <p className="text-lg font-bold tabular-nums text-orange-600">{fmt(receivables.summary['60_days'])}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">91-120 Days</p>
              <p className="text-lg font-bold tabular-nums text-red-500">{fmt(receivables.summary['90_days'])}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Over 120 Days</p>
              <p className="text-lg font-bold tabular-nums text-red-700">{fmt(receivables.summary.over_90)}</p>
            </CardContent></Card>
            <Card className="bg-red-50 dark:bg-red-950/20"><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Grand Total</p>
              <p className="text-lg font-bold tabular-nums text-red-600" data-testid="receivables-total">{fmt(receivables.summary.grand_total)}</p>
              <p className="text-xs text-muted-foreground">{receivables.summary.total_customers} customers</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" /> Receivables by Customer
            </CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="receivables-table">
                  <thead className="sticky top-0 bg-card"><tr className="border-b text-muted-foreground text-left">
                    <th className="pb-2 font-medium">Customer</th>
                    <th className="pb-2 font-medium text-right">Invoices</th>
                    <th className="pb-2 font-medium text-right">Current</th>
                    <th className="pb-2 font-medium text-right">31-60d</th>
                    <th className="pb-2 font-medium text-right">61-90d</th>
                    <th className="pb-2 font-medium text-right">91-120d</th>
                    <th className="pb-2 font-medium text-right">120d+</th>
                    <th className="pb-2 font-medium text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {receivables.customers.map((c, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1.5 font-medium max-w-[180px] truncate">{c.customer_name}</td>
                        <td className="py-1.5 text-right text-muted-foreground">{c.invoice_count}</td>
                        <td className="py-1.5 text-right tabular-nums">{c.current > 0 ? fmt(c.current) : '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-amber-600">{c['30_days'] > 0 ? fmt(c['30_days']) : '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-orange-600">{c['60_days'] > 0 ? fmt(c['60_days']) : '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-red-500">{c['90_days'] > 0 ? fmt(c['90_days']) : '—'}</td>
                        <td className="py-1.5 text-right tabular-nums text-red-700">{c.over_90 > 0 ? fmt(c.over_90) : '—'}</td>
                        <td className="py-1.5 text-right tabular-nums font-semibold">{fmt(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stock Movements */}
      {activeTab === 'stock-movements' && stockMovements && !loading && (
        <div className="space-y-6" data-testid="stock-movements-report">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Stock In</p>
              <p className="text-xl font-bold text-green-600" data-testid="stock-in-total">+{stockMovements.summary.total_in.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">units received</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Stock Out</p>
              <p className="text-xl font-bold text-red-600" data-testid="stock-out-total">-{stockMovements.summary.total_out.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">units shipped</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Net Change</p>
              <p className={`text-xl font-bold ${stockMovements.summary.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stockMovements.summary.net_change >= 0 ? '+' : ''}{stockMovements.summary.net_change.toLocaleString()}
              </p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Units Sold</p>
              <p className="text-xl font-bold">{stockMovements.summary.total_sold.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">from sales orders</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-1">Total Movements</p>
              <p className="text-xl font-bold">{stockMovements.summary.total_movements.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">adjustments recorded</p>
            </CardContent></Card>
          </div>

          {/* Stock Flow Chart - Area */}
          {stockMovements.daily_trend.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" /> Daily Stock Flow
              </CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320} data-testid="stock-flow-chart">
                  <AreaChart data={stockMovements.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={(v) => `Date: ${v}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="stock_in" name="Stock In" stroke="#16a34a" fill="#16a34a" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="stock_out" name="Stock Out" stroke="#dc2626" fill="#dc2626" fillOpacity={0.1} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Movement by Type - Pie Chart */}
            {stockMovements.by_type.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Movement by Type</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={stockMovements.by_type.map(t => ({
                          name: t.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                          value: t.total_qty,
                          count: t.count,
                        }))}
                        cx="50%" cy="50%"
                        innerRadius={60} outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {stockMovements.by_type.map((_, i) => (
                          <Cell key={i} fill={['#16a34a', '#dc2626', '#2563eb', '#d97706', '#7c3aed', '#0891b2', '#be185d', '#65a30d'][i % 8]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val) => `${val} units`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Units Sold vs Received - Bar Chart */}
            {stockMovements.daily_trend.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">Sales vs Receiving</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={stockMovements.daily_trend.filter(d => d.units_sold > 0 || d.units_received > 0)}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="units_received" name="Received" fill="#16a34a" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="units_sold" name="Sold" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Top Movers Table */}
          {stockMovements.top_movers.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" /> Top Movers
              </CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="top-movers-table">
                    <thead><tr className="border-b text-muted-foreground text-left">
                      <th className="pb-2 font-medium">#</th>
                      <th className="pb-2 font-medium">Product</th>
                      <th className="pb-2 font-medium">SKU</th>
                      <th className="pb-2 font-medium text-right">In</th>
                      <th className="pb-2 font-medium text-right">Out</th>
                      <th className="pb-2 font-medium text-right">Net</th>
                      <th className="pb-2 font-medium text-right">Movements</th>
                    </tr></thead>
                    <tbody>
                      {stockMovements.top_movers.map((m, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1.5 text-muted-foreground">{i + 1}</td>
                          <td className="py-1.5 font-medium max-w-[200px] truncate">{m.name}</td>
                          <td className="py-1.5 text-muted-foreground">{m.sku}</td>
                          <td className="py-1.5 text-right text-green-600">+{m.total_in}</td>
                          <td className="py-1.5 text-right text-red-600">-{m.total_out}</td>
                          <td className={`py-1.5 text-right font-medium ${m.net_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {m.net_change >= 0 ? '+' : ''}{m.net_change}
                          </td>
                          <td className="py-1.5 text-right">{m.movement_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {stockMovements.daily_trend.length === 0 && stockMovements.top_movers.length === 0 && (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              No stock movement data found for this period. Try expanding the date range.
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
